import { SidebarSimpleIcon } from '@phosphor-icons/react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { trackProductEvent } from '@/lib/analytics/product-events';
import { openHub } from '@/stores/_panel-mutex';
import { useProjectHubStore } from '@/stores/useProjectHubStore';

interface ProjectHubToggleButtonProps {
  isHubOpen: boolean;
  planId: string | null;
}

/**
 * PH-1 (PH-5.1) page-header toggle for the project hub on /create.
 *
 * Specialized to one meaning: "toggle the project rail". Caller renders
 * this only when the chat is project-bound (PageHeaderManager gates via
 * `isProjectBound`).
 *
 * Audit pass 5 (2026-05-08, user feedback "padding outside the icon and
 * the icon group is too big, overpowering the page"): replaced the
 * hand-rolled size-9 button with the canonical <CircleIconButton>
 * primitive. That gives size-9 on mobile, size-8 (32px) on desktop —
 * one notch smaller and aligned with every other header icon button
 * in the app. Also drops the bg-muted active fill: the panel itself
 * is the active-state evidence; reinforcing with a button highlight
 * is redundant noise.
 *
 * a11y:
 * - aria-label: "Open project rail" / "Close project rail"
 * - aria-pressed reports active state for screen readers
 * - data-ph1-hub-toggle marks this as the focus-return target for
 *   ProjectHubMount when the hub closes (Task 4.7)
 */
export function ProjectHubToggleButton({
  isHubOpen,
  planId,
}: ProjectHubToggleButtonProps): JSX.Element {
  const buttonLabel = isHubOpen ? 'Close project rail' : 'Open project rail';

  const handleToggle = () => {
    if (isHubOpen) {
      useProjectHubStore.getState().setOpen(false, planId);
      void trackProductEvent('project_hub_closed', { project_id: planId, trigger: 'user' });
    } else {
      openHub(planId);
      void trackProductEvent('project_hub_opened', { project_id: planId, trigger: 'user' });
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CircleIconButton
          ariaLabel={buttonLabel}
          aria-pressed={isHubOpen}
          data-ph1-hub-toggle=""
          onClick={handleToggle}
        >
          <SidebarSimpleIcon weight="regular" />
        </CircleIconButton>
      </TooltipTrigger>
      <TooltipContent>{buttonLabel}</TooltipContent>
    </Tooltip>
  );
}
